// frontend/packages/business-console/src/components/common/ErrorBoundary.js

import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";

const isProduction = process.env.NODE_ENV === "production";

function normalizeError(err) {
  if (!err) return { name: "Error", message: "Unknown error", stack: "" };

  if (err instanceof Error) {
    return {
      name: err.name || "Error",
      message: err.message || "Unknown error",
      stack: err.stack || "",
    };
  }

  // Some libraries throw strings/objects
  try {
    return {
      name: "Error",
      message: typeof err === "string" ? err : JSON.stringify(err),
      stack: "",
    };
  } catch {
    return { name: "Error", message: "Unknown error", stack: "" };
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null, // normalized error object
      componentStack: "",
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: normalizeError(error) };
  }

  componentDidCatch(error, info) {
    const normalized = normalizeError(error);
    const componentStack = info?.componentStack || "";

    // Dev-only logging
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.error("ErrorBoundary caught an error:", normalized);
      // eslint-disable-next-line no-console
      console.error("Component stack trace:", componentStack);
    }

    this.setState({ error: normalized, componentStack });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoToLogin = () => {
    window.location.assign("/login");
  };

  render() {
    const { hasError, error, componentStack } = this.state;

    if (!hasError) return this.props.children;

    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 720 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Something went wrong
          </Typography>

          <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
            The console hit an unexpected error. Reload the page. If it keeps happening, sign out and
            sign back in.
          </Typography>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              mb: 2,
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Error
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                whiteSpace: "pre-wrap",
              }}
            >
              {error?.name ? `${error.name}: ` : ""}
              {error?.message || "Unknown error"}
            </Typography>

            {!isProduction && (componentStack || error?.stack) ? (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Debug (dev only)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    whiteSpace: "pre-wrap",
                    opacity: 0.9,
                  }}
                >
                  {componentStack || error?.stack}
                </Typography>
              </>
            ) : null}
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button variant="contained" onClick={this.handleReload}>
              Reload
            </Button>
            <Button variant="outlined" onClick={this.handleGoToLogin}>
              Go to Login
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }
}

export default ErrorBoundary;